import type { BooleanAnalytics, LocalityAnalytics } from 'lib/dot/types.ts';
import type { URLFilterParams } from 'utils/url.ts';
import { parseNumber } from 'utils/parseNumber.ts';
import { PRICE } from 'utils/constants.ts';
import type { OfferData, OfferLocation } from 'service/offer/model.ts';
import type { LocalityData } from 'service/locality/model.ts';
import { STATE } from 'utils/state';
import type { OfferStatusAnalytics } from './filter/utils';

export function offerLocationToLocalityAnalytics(
	location: OfferLocation,
	matchedLocalityId?: number | null,
): LocalityAnalytics {
	const locality = location.locality;

	return {
		districtId: locality.districtId,
		localityType: locality.entityType,
		muniId: locality.municipalityId,
		regionId: locality.regionId,
		main: location.main,
		...(matchedLocalityId !== null && { matched: matchedLocalityId === location.id }),
	};
}

export function offerToLocalityAnalytics(
	offer: Pick<OfferData, 'localities' | 'locality'>,
	matchedLocalityId?: number | null,
): LocalityAnalytics[] {
	return (
		offer.localities?.map((locality) => offerLocationToLocalityAnalytics(locality, matchedLocalityId)) ??
		(offer.locality && [localityToAnalyticsLocality(offer.locality)]) ??
		[]
	);
}

export function localityToAnalyticsLocality(locality: LocalityData): LocalityAnalytics {
	return {
		districtId: locality.districtId,
		localityType: locality.entityType,
		muniId: locality.municipalityId,
		regionId: locality.regionId,
	};
}

export type SearchFiltersAnalytics = {
	'with-deal': BooleanAnalytics;
	categoryId?: string;
	categoryName?: string;
	expiring?: BooleanAnalytics;
	kde?: string;
	locality?: string; // In this case this is the locality's SEO name
	phrase?: string;
	price?: 'custom' | 'for-free'; // custom | for-free | <empty> if without limits
	priceFrom?: number;
	priceTo?: number;
	radius?: number;
	status?: OfferStatusAnalytics;
	usedGoods?: BooleanAnalytics;
};

/**
 * In this case undefined data are not desired for w/e reason, so omit them. Only 'with-deal' is sent not matter what.
 * This specification is copied without questioning it from old IMA.js implementation.
 * @param filters
 */
export function localFiltersToAnalyticsFilters(filters: URLFilterParams): SearchFiltersAnalytics {
	const prices: any = {};

	if (filters.price.type === PRICE.FREE) {
		prices.price = 'for-free';
	} else if (filters.price.type !== PRICE.NO_LIMIT) {
		prices.price = 'custom';
		prices.priceFrom = filters.price.from;
		prices.priceTo = filters.price.to;
	}

	return {
		...(filters.category?.id && { categoryName: filters.category.seoName }),
		// ID: 0 => cela-cr, and we do not want to include that
		...(filters.locality?.id && {
			locality: filters.locality.seoName,
			radius: parseNumber(filters.radius?.seoName) ?? 5,
		}),
		...(filters.kde?.seoName && { kde: filters.kde.seoName }),
		...prices,
		...(filters.hledej?.seoName && { phrase: filters.hledej?.seoName }),
		...(filters.isAdmin && { expiring: filters.expiring?.value ? 1 : 0 }),
		'with-deal': filters.dohodou?.seoName === 'bez' ? 0 : 1,
		usedGoods: filters.rozbalene?.seoName === 'ano' ? 1 : 0,
		status: filters.status === STATE.INACTIVE ? 'inactive' : 'active',
	};
}
